import { useEffect, useState } from 'react'
import { AtLink, AtLinkVariant, useSessionStorage } from '@curran-catalog/curran-atomic-library'
import { useCart } from '@hooks/use-cart'
import classNames from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { CartItem } from 'types'
import { Quantity } from '@components/product-detail-options'
import { detectSiteDomain } from '@utils/detect-site-domain'
import { sendCartAnalyticsEvent } from '@utils/analytics/events/cart/send-event'
import { CartEventTypes } from '../../types/analytics/analytics-data'

interface ItemProps {
  item: CartItem
  itemIdx: number
  isEditable: boolean
  isProdRush: boolean
}

const { furnitureSite, synsisalSite, flooringSite, sisalcarpetSite } = detectSiteDomain()

export const Item = ({ item, isEditable, itemIdx, isProdRush }: ItemProps) => {
  const { updateQuantity, removeItem } = useCart()
  const [helperText, setHelperText] = useState<string>('')
  const [minQuantity, setMinQuantity] = useState<number>(1)
  const [previousListRoute] = useSessionStorage('x-previousListTitleRoute', '')

  const maxQuantity = item.metadata?.category2 === 'Tiles' ? 9999999999 : 99

  const handleQuantityChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const quantity = Number(event.target.value)
    const prevQuantity = item.quantity
    const updatedItems = await updateQuantity({ id: item.id, quantity: quantity })
    const updatedItem = updatedItems && updatedItems.items.find((item) => item.id === item.id)
    const isAdding = prevQuantity < quantity
    const isRemoving = prevQuantity > quantity

    if (isAdding) {
      const newQuantity = quantity - prevQuantity
      if ((synsisalSite || furnitureSite) && updatedItem) {
        sendCartAnalyticsEvent(
          updatedItem,
          CartEventTypes.ADD_TO_CART,
          itemIdx,
          newQuantity,
          synsisalSite ? previousListRoute : undefined,
        )
      }
    }
    if (isRemoving) {
      const newQuantity = prevQuantity - quantity
      if ((synsisalSite || flooringSite || sisalcarpetSite) && updatedItem) {
        sendCartAnalyticsEvent(updatedItem, CartEventTypes.REMOVE_FROM_CART, itemIdx, newQuantity, previousListRoute)
      }
    }
  }

  const handleIncreaseQuantity = async () => {
    const quantityUpdate = item.quantity + 1
    const prevQuantity = item.quantity
    const updatedItems = await updateQuantity({ id: item.id, quantity: Number(quantityUpdate) })
    const updatedItem = updatedItems && updatedItems.items.find((item) => item.id === item.id)

    const newQuantity = Number(quantityUpdate) - prevQuantity
    if ((furnitureSite || synsisalSite) && updatedItem) {
      sendCartAnalyticsEvent(
        updatedItem,
        CartEventTypes.ADD_TO_CART,
        itemIdx,
        synsisalSite ? 1 : newQuantity,
        synsisalSite ? previousListRoute : undefined,
      )
    }
  }

  const handleDecreaseQuantity = async () => {
    const quantityUpdate = item.quantity - 1
    await updateQuantity({ id: item.id, quantity: Number(quantityUpdate) })
    if ((synsisalSite || flooringSite || sisalcarpetSite) && item) {
      sendCartAnalyticsEvent(
        item,
        CartEventTypes.REMOVE_FROM_CART,
        itemIdx,
        1,
        synsisalSite ? previousListRoute : undefined,
      )
    }
  }

  const handleRemoveItem = async () => {
    await removeItem({ id: item.id })
    if ((synsisalSite || flooringSite || sisalcarpetSite) && item) {
      sendCartAnalyticsEvent(
        item,
        CartEventTypes.REMOVE_FROM_CART,
        itemIdx,
        item.quantity,
        synsisalSite ? previousListRoute : undefined,
      )
    }
  }

  const quantityOptions = Array.from(Array(50).keys()).map((i) => ({ value: i + 1, label: (i + 1).toString() }))

  if (item.metadata?.minimumQuantity) {
    quantityOptions.splice(0, item.metadata.minimumQuantity - 1)
  }

  // /api/quantity-product-info?productID=FLC-00000320
  useEffect(() => {
    const fetchQuantityInfo = async () => {
      if (!item.metadata?.contentful_id) return

      const response = await fetch(`/api/quantity-product-info?productID=${item.metadata?.contentful_id}`)
      const data = await response.json()

      if (data.code !== 200) {
        // eslint-disable-next-line no-console
        console.error('Error fetching quantity info')
        return
      }
      setMinQuantity(data.data.minQuantity)
      setHelperText(data.data.quantityHelperText)
    }

    fetchQuantityInfo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <li className="flex py-6 sm:py-8">
        {item.imageSrc ? (
          <div className="flex-shrink-0">
            <Image
              src={item.imageSrc}
              alt={item.imageAlt}
              width="110"
              height="110"
              className="h-24 w-24 rounded-lg object-cover object-center sm:h-32 sm:w-32"
            />
          </div>
        ) : (
          <div className="h-[110px] w-[110px] bg-white"></div>
        )}

        <div className="sm:static ml-4 flex flex-1 flex-col justify-between sm:ml-6">
          <div>
            <div className="flex justify-between sm:grid sm:grid-cols-2">
              <div className="">
                <h3 className="text-sm">
                  {item.metadata?.link ? (
                    <Link href={item.metadata.link} className="font-medium text-dark hover:text-primary-hover">
                      {item.description}
                    </Link>
                  ) : (
                    <span className="font-medium text-dark">{item.description}</span>
                  )}
                </h3>
                <p className="mt-1 text-sm text-gray-500">{item.details}</p>
                <div>
                  {item.shippingTimes && (
                    <p className={`mt-1 text-sm text-gray-500 `}>
                      Lead Time + Transit
                      {!isProdRush ? <b> {item.shippingTimes}</b> : <b>{' 2 - 3 Weeks'}</b>}{' '}
                    </p>
                  )}
                </div>

                {item.metadata?.notes && (
                  <p className="mt-1 text-sm text-outlined-medium">
                    <span className="text-gray">Note:</span> {item.metadata.notes}
                  </p>
                )}
              </div>

              <div className="flex justify-end items-start text-right text-sm font-medium pl-3">
                {(!isEditable || !item.isQuantityEditable) && (
                  <div
                    aria-label="Quantity"
                    className="flex items-center justify-center h-5 w-7 mr-2 bg-outlined-gray rounded-full text-gray-900 text-sm font-normal"
                  >
                    {item.quantity}x
                  </div>
                )}
                <div>
                  <p className={'text-gray-900 ' + (item.showDiscount ? 'line-through' : '')}>{item.price}</p>
                  {item.showDiscount && <p className="text-success">{item.netPrice}</p>}
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              {isEditable && item.isQuantityEditable && (
                <Quantity
                  min={minQuantity}
                  max={maxQuantity}
                  value={item.quantity}
                  helperText={helperText}
                  handleChange={handleQuantityChange}
                  increase={handleIncreaseQuantity}
                  decrease={handleDecreaseQuantity}
                />
              )}
              {isEditable && item.isRemovable && (
                <AtLink
                  className={classNames(item.isQuantityEditable && 'ml-4 sm:ml-0 sm:mt-2', 'text-sm')}
                  variant={AtLinkVariant.DEFAULT}
                  onClick={handleRemoveItem}
                >
                  Remove
                </AtLink>
              )}
            </div>
          </div>
        </div>
      </li>
    </div>
  )
}
